import { useEffect, useState } from 'react';
import s from './AddressCard.module.scss';
import { ReactComponent as HomeIcon } from '../../../Icons/Home.svg';
import { ReactComponent as EditIcon } from '../../../Icons/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../Icons/Delete.svg';
import { ReactComponent as WorkIcon } from '../../../Icons/Work.svg';
import { ReactComponent as OtherIcon } from '../../../Icons/Location-Outlined.svg';
import useSessionStorage from '../../../hooks/useStorage';
import { getAddressFromLatLng } from '../../../services';

/* -----------------------------
   Utils: Haversine Distance
------------------------------ */
const toRad = (value) => (value * Math.PI) / 180;

const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const formatDistance = (meters) => {
  if (meters == null || Number.isNaN(meters)) return '';

  if (meters < 1000) {
    const m = Math.round(meters);
    const s = m === 1 ? 'mt' : 'mts';
    return `${m} ${s}`;
  }

  const km = Math.round(meters / 1000);
  const s = km === 1 ? 'km' : 'kms';
  return `${km} ${s}`;
};

/* -----------------------------
   Address Card Component
------------------------------ */
const AddressCard = ({
  title,
  isDefault,
  address,
  addressId,
  lat,
  long,
  phone,
  onEdit,
  onDelete,
  pincode,
  onClick,
  isSelected,
  serviceable,
  noMargin = false,
}) => {
  const [distance, setDistance] = useState('');
  const { setToStorage } = useSessionStorage();

  useEffect(() => {
    if (lat == null || long == null || !navigator.geolocation) return;

    const onSuccess = ({ coords }) => {
      const meters = getDistanceInMeters(
        coords.latitude,
        coords.longitude,
        Number(lat),
        Number(long)
      );
      setDistance(formatDistance(meters));
    };

    navigator.geolocation.getCurrentPosition(onSuccess);
  }, [lat, long]);

  const handleSelect = () => {
    getAddressFromLatLng(lat, long)
      .then((res) => {
        const payload = {
          title,
          isDefault,
          address,
          lat,
          long,
          phone,
          pincode,
          addressId,
          city: res?.result?.city,
          state: res?.result?.state,
          formattedAddress: res?.result?.formattedAddress,
        };

        setToStorage('selected_address', payload);

        // ✅ Fire event AFTER storage update
        window.dispatchEvent(new Event('location_selected'));
        onClick?.();
      })
      .catch(() => {
        const payload = {
          title,
          isDefault,
          address,
          lat,
          long,
          phone,
          pincode,
          addressId,
          city: '',
          state: '',
          formattedAddress: address,
        };

        setToStorage('selected_address', payload);

        // ✅ Fire event AFTER storage update
        window.dispatchEvent(new Event('location_selected'));
        onClick?.();
      });
  };

  return (
    <div
      className={s.card}
      style={{ margin: noMargin ? '0' : '16px', cursor: 'pointer' }}
      onClick={() => {
        if (
          serviceable === undefined ||
          serviceable === true ||
          serviceable === 1
        ) {
          handleSelect();
        }
      }}
      s
    >
      <div className={s.top}>
        {isSelected && <div className={s.selectedBadge}>Selected</div>}
        <div className={s.left}>
          <div className={s.iconContainer}>
            {title === 'Home' ? (
              <HomeIcon height={18} width={18} color="var(--primary-button, #714fff)" />
            ) : title === 'Work' ? (
              <WorkIcon height={18} width={18} color="var(--primary-button, #714fff)" />
            ) : (
              <OtherIcon height={18} width={18} color="var(--primary-button, #714fff)" />
            )}

            {distance && <span className={s.distance}>{distance}</span>}
          </div>

          <div className={s.content}>
            <div className={s.header}>
              <span className={s.title}>{title}</span>
              {isDefault && <span className={s.default}>(Default)</span>}
            </div>

            <p className={s.address}>{address}</p>
            {phone && <p className={s.phone}>Phone: {phone}</p>}
            {pincode && <p className={s.pincode}>{pincode}</p>}
          </div>
        </div>

        <div className={s.right}>
          <div className={s.actions}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.();
              }}
              className={s.editBtn}
            >
              <EditIcon height={18} width={18} />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.();
              }}
              className={s.deleteBtn}
            >
              <DeleteIcon height={18} width={18} />
            </button>
          </div>
        </div>
      </div>
      {(serviceable === false || serviceable === 0) && (
        <div className={s.nonServiceable}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="10" stroke="#DC2C2C" stroke-width="2" />
            <line
              x1="12"
              y1="10"
              x2="12"
              y2="16"
              stroke="#DC2C2C"
              stroke-width="2"
              stroke-linecap="round"
            />
            <circle cx="12" cy="7" r="1.2" fill="#DC2C2C" />
          </svg>
          {serviceable === 0
            ? 'The selected center do not service at this location'
            : 'Selected partner does not service at this location'}
        </div>
      )}
    </div>
  );
};

export default AddressCard;
